import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import SelectAgreement from "components/SelectAgreement";
import SelectResource from "components/SelectResource";
import SelectSku from "components/SelectSku";
import { FormApi } from "final-form";
import arrayMutators from "final-form-arrays";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AgreementResponse,
  ISkuUserAgreementInfo,
  ResourceV2ListItem,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import {
  TemplateItem,
  UserAgreementAdditionalFields,
} from "./UserAgreementAdditionalFields";
import { AgreementStatusIds } from "types/common-types";

type SkuUserAgreementFormProps = {
  skuUserAgreement?: ISkuUserAgreementInfo;
  onSubmit: (values: UserAgreementFormValues) => void;
  onAgreementSelected?: (agreement: AgreementResponse | null) => void;
  onFacultySelected?: (faculty: ResourceV2ListItem | null) => void;
};

export type UserAgreementFormValues = {
  id?: string;
  skuId?: string;
  agreementId?: string;
  dueDate?: Date;
  organizationUserId?: string;
  organizationId?: string;
  additionalFields: {
    key: string;
    key_type: string;
    value: string | number | boolean | Date;
  }[];
};

export const UserAgreementForm = ({
  skuUserAgreement,
  onSubmit,
  onAgreementSelected,
  onFacultySelected,
}: SkuUserAgreementFormProps) => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const [templateItems, setTemplateItems] = useState<TemplateItem[]>([]);
  const [faculty, setFaculty] = useState<ResourceV2ListItem | null>(null);

  const { skuIdParam, organizationUserIdParam } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      skuIdParam: params.get("skuId"),
      organizationUserIdParam: params.get("userId"),
    };
  }, [location.search]);

  useEffect(() => {
    organizationUserIdParam &&
      setFaculty({ organizationUserId: organizationUserIdParam } as ResourceV2ListItem);
  }, [organizationUserIdParam]);

  const { skuUserAgreementsUrl } = useGlobalContext();

  const { organizationId } = contextStore.useState();

  const isSkuIdFromUrl = !!skuIdParam;
  const isUserIdFromUrl = !!organizationUserIdParam;

  const init = useMemo(() => {
    const baseValues = skuUserAgreement
      ? pick(skuUserAgreement, [
          "id",
          "skuId",
          "dueDate",
          "agreementId",
          "organizationUserId",
        ])
      : {
          skuId: skuIdParam ?? undefined,
          organizationUserId: organizationUserIdParam ?? undefined,
        };

    return {
      ...baseValues,
      additionalFields:
        skuUserAgreement?.skuUserAgreementValues?.map((x) => ({
          key: x.name,
          value: x.value,
          type: x.type,
        })) ?? [],
    };
  }, [skuUserAgreement, skuIdParam, organizationUserIdParam]);

  const onAgreementChange = (
    value: AgreementResponse | null,
    form: FormApi<UserAgreementFormValues>
  ) => {
    onAgreementSelected?.(value);

    const parsed = value?.manualReplacements
      ? JSON.parse(value.manualReplacements)
      : null;
    const template: TemplateItem[] = Array.isArray(parsed?.template)
      ? parsed.template
      : [];

    setTemplateItems(template);

    // The inputs are bound by position, so the array has to be rebuilt on every
    // agreement change or the keys of the previous agreement are submitted.
    form.change(
      "additionalFields",
      template.map((item) => ({
        key: item.key,
        key_type: item.key_type,
        value: "",
      }))
    );
  };

  const required = (value: any) =>
    value === undefined || value === null || value === ""
      ? "Required"
      : undefined;

  return (
    <>
      <Form
        onSubmit={onSubmit}
        mutators={{ ...arrayMutators }}
        initialValues={init}
        render={({
          handleSubmit,
          submitting,
          submitError,
          values,
          form,
          invalid,
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="skuId" validate={required}>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          required
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSku
                            {...input}
                            required
                            organizationId={organizationId}
                            label={t({ id: "skuLabel" })}
                            disabled={isSkuIdFromUrl}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field
                    name="organizationUserId"
                    key={values.skuId}
                    validate={required}
                  >
                    {({ input, meta }) => {
                      return (
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            required
                            error={meta.error && meta.submitFailed}
                          >
                            <SelectResource
                              {...input}
                              value={faculty}
                              textFieldProps={{
                                error: meta.error && meta.submitFailed,
                              }}
                              filter={{ skuId: values.skuId, isLinkedWithUser: true }}
                              disabled={isUserIdFromUrl}
                              required
                              onChange={(
                                resource: ResourceV2ListItem | null
                              ) => {
                                input.onChange(resource?.organizationUserId);
                                form.change(
                                  "organizationId",
                                  resource?.organizationId
                                );
                                onFacultySelected?.(resource);
                              }}
                            />
                            {meta.error && meta.submitFailed && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      );
                    }}
                  </Field>

                  <Field name="dueDate" isDate validate={required}>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          required
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "dueDate" })}
                            format="MM/dd/yyyy"
                            slotProps={{
                              textField: {
                                required: true,
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                  <Field name="agreementId" validate={required}>
                    {({ input, meta }) => {
                      return (
                        <Grid item xs={12}>
                          <FormControl
                            required
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <SelectAgreement
                              {...input}
                              textFieldProps={{
                                error: meta.error && meta.submitFailed,
                              }}
                              required
                              parentOnChange={(agreement) =>
                                onAgreementChange(agreement, form)
                              }
                              filter={{
                                agreementStatusIds: [AgreementStatusIds.Active],
                              }}
                            />
                            {meta.error && meta.submitFailed && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      );
                    }}
                  </Field>

                  <Grid item xs={12}>
                    <Box mt={3} />
                    <FieldArray name="additionalFields">
                      {({ meta }) => {
                        return (
                          <FormControl fullWidth>
                            <FormLabel
                              error={meta.error && meta.submitFailed}
                              required
                            >
                              <T id="additionalFields" />
                            </FormLabel>
                            <UserAgreementAdditionalFields
                              items={templateItems}
                            />
                            {meta.error && meta.submitFailed && (
                              <FormHelperText error>
                                {meta.error}
                              </FormHelperText>
                            )}
                          </FormControl>
                        );
                      }}
                    </FieldArray>
                  </Grid>

                  {submitError && <Alert severity="error">{submitError}</Alert>}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  disabled={submitting}
                  onClick={() => navigate(skuUserAgreementsUrl())}
                >
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={submitting || invalid}
                  startIcon={submitting && <CircularProgress size={24} />}
                >
                  <T id="assignAgreement" />
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </>
  );
};
